import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { SHIPPING_DETAILS, returnPolicyFor } from "@/lib/merchantSchema";

const SITE_URL = "https://untamedsportsgear.co.za";
const DEFAULT_IMAGE = `${SITE_URL}/logo.webp`;
const BRAND = "Untamed Gear";

/**
 * Per-page SEO Head:
 * - Injects unique <title>, meta description, canonical URL
 * - Emits Open Graph + Twitter card tags
 * - Optionally emits JSON-LD structured data (Product, BreadcrumbList, etc.)
 *
 * Because this is a client-rendered SPA, these tags are set at runtime.
 * Combined with the static <meta> defaults in public/index.html and the
 * XML sitemap at /sitemap.xml, this gives Google enough to index each page
 * with the correct meta.
 */
export default function SEOHead({
  title,
  absoluteTitle,
  description,
  image,
  keywords,
  type = "website",
  canonicalPath,
  jsonLd,
  noindex = false,
}) {
  const location = useLocation();
  const path = canonicalPath || location.pathname || "/";
  const url = `${SITE_URL}${path === "/" ? "" : path}`;
  const fullTitle = absoluteTitle || (title ? `${title} | ${BRAND}` : `${BRAND} — Rugby, Hockey & Netball Kit made in South Africa`);
  const desc = description || "Untamed Gear — South Africa's rugby, hockey and netball apparel brand. Rugby shorts, rugby jerseys, hockey dresses, netball skorts, scrum caps, shoulder pads. Colour-match your team kit.";
  const ogImage = image || DEFAULT_IMAGE;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={url} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={BRAND} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:locale" content="en_ZA" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={ogImage} />

      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(Array.isArray(jsonLd) ? jsonLd : [jsonLd])}
        </script>
      )}
    </Helmet>
  );
}

// Global Organization JSON-LD — mount once from App
export function OrganizationJsonLd() {
  const data = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: BRAND,
    url: SITE_URL,
    logo: DEFAULT_IMAGE,
    slogan: "Uncaged. Untamed. Uncompromised.",
    sameAs: [
      "https://www.tiktok.com/@untamed.gear",
      "https://www.instagram.com/untamedsportsgear",
      "https://www.facebook.com/share/1GHfSftdJ4/",
      "https://www.youtube.com/@Untamedsportsgear",
    ],
    contactPoint: [{
      "@type": "ContactPoint",
      contactType: "customer service",
      areaServed: "ZA",
      availableLanguage: ["English"],
    }],
    address: {
      "@type": "PostalAddress",
      addressCountry: "ZA",
    },
  };
  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(data)}</script>
    </Helmet>
  );
}

export function productJsonLd(product, path) {
  if (!product) return null;
  const url = `${SITE_URL}${path}`;
  const priceValid = new Date();
  priceValid.setFullYear(priceValid.getFullYear() + 1);
  const validFrom = product.created_at || null;
  const inStock = (product.stock ?? 0) > 0;
  const displayPrice = product.is_special && product.special_price ? product.special_price : product.price;

  const returnPolicy = returnPolicyFor(product.return_eligibility);
  const shippingDetails = SHIPPING_DETAILS;

  // Real reviews only — never inject fake aggregateRating
  const hasReviews = Number(product.review_count) > 0 && Number(product.avg_rating) > 0;

  const doc = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.name,
    description: product.description,
    image: [product.image_url, ...(product.images || [])].filter(Boolean).map(u => u.startsWith("http") ? u : `${SITE_URL}${u}`),
    brand: { "@type": "Brand", name: BRAND },
    sku: product.id,
    category: product.category,
    offers: {
      "@type": "Offer",
      url,
      priceCurrency: "ZAR",
      price: displayPrice,
      availability: inStock ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
      itemCondition: "https://schema.org/NewCondition",
      priceValidUntil: priceValid.toISOString().split("T")[0],
      ...(validFrom ? { validFrom } : {}),
      hasMerchantReturnPolicy: returnPolicy,
      shippingDetails,
    },
  };
  if (hasReviews) {
    doc.aggregateRating = {
      "@type": "AggregateRating",
      ratingValue: product.avg_rating,
      reviewCount: product.review_count,
    };
  }
  return doc;
}

export function breadcrumbJsonLd(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: `${SITE_URL}${it.path}`,
    })),
  };
}
